// SETTINGS GUARD — Permission-Based Access Control
// Only users with 'settings.manage' or 'configuration.manage' permissions can access Settings routes
// Redirects unauthorized users with access denied message

import React from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import { usePermission } from '@/context/PermissionsContext';
import { hasValidToken } from '@/services/authService';
import AccessDenied from '@/components/common/organisms/AccessDenied';
import LoadingSpinner from '@/components/common/atoms/LoadingSpinner';

interface SettingsGuardProps {
  children: React.ReactNode;
}

const SettingsGuard: React.FC<SettingsGuardProps> = ({ children }) => {
  const { user } = useAuth();
  const { can, isLoaded } = usePermission();

  // No user, or no live token behind it, means no session — back to login.
  if (!user || !hasValidToken()) {
    return <Navigate to="/login" replace />;
  }

  // Wait for permissions to finish loading
  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  // Check if user has settings or configuration management permission
  if (!can('settings', 'manage') && !can('configuration', 'manage')) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default SettingsGuard;
